import { FC } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { InboxArrowDownIcon, UserGroupIcon } from '@heroicons/react/24/outline'

export type InfoStatItem = {
  title: string
  value: number
  icon?: any
  color?: string
}

export type InfoStatsGridProps = {
  items: InfoStatItem[]
  iconStyle?: 'solid' | 'simple'
  layout?: 'centered' | 'spread'
}

export const InfoStatsGrid: FC<InfoStatsGridProps> = ({
  items,
  iconStyle = 'simple',
  layout = 'spread',
}) => {
  const navigate = useNavigate()

  const goToConsult = (filterType: number) => {
    const today = moment()
    const from = moment(today).startOf('month').format('YYYY-MM-DD')
    const to = moment(today).format('YYYY-MM-DD')

    const params = new URLSearchParams()

    if (filterType !== 1 && filterType !== 3) {
      params.set('from', from)
      params.set('to', to)
      params.set('dateField', 'creationDate')
    }

    params.set('shipmentFilterType', filterType.toString())
    navigate(`/shipments/consult?${params.toString()}`)
  }

  const fallbackIcons = [InboxArrowDownIcon, UserGroupIcon]

  const getGridClass = () => {
    const count = items.length
    if (count === 1) return 'grid-cols-1'
    if (count === 2) return 'grid-cols-1 sm:grid-cols-2'
    if (count === 3) return 'grid-cols-1 sm:grid-cols-3'
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  }

  return (
    <div className={`grid gap-9 ${getGridClass()}`}>
      {items.map((item, idx) => {
        const Icon =
          (item.icon as any) || fallbackIcons[idx % fallbackIcons.length]
        const accent = 'bg-main-500'

        const isPendingInvoice = item.title.toLowerCase().includes('facturar')
        const isInvoiced = item.title.toLowerCase().includes('facturadas')
        const isInTransit = item.title.toLowerCase().includes('tránsito')

        const filterType = isPendingInvoice
          ? 1
          : isInvoiced
            ? 2
            : isInTransit
              ? 3
              : undefined
        const isInteractive = filterType !== undefined

        return (
          <article
            key={item.title}
            className={`group/article relative overflow-hidden rounded-xl border border-main-100/60 bg-white/75 backdrop-blur shadow-sm transition ${
              isInteractive
                ? 'hover:shadow-md hover:-translate-y-1 cursor-pointer'
                : 'cursor-default hover:-translate-y-1 hover:shadow-md'
            }`}
            onClick={
              isInteractive && filterType
                ? () => goToConsult(filterType)
                : undefined
            }
          >
            <div className="absolute inset-x-0 top-0 " />
            <div className="absolute -right-10 -top-10 h-24 w-24 rounded-full bg-main-500/10 blur-3xl" />

            {layout === 'centered' ? (
              <div className="relative flex flex-col items-center justify-center p-6 text-center">
                <div className="flex items-center gap-3 mb-2">
                  {iconStyle === 'solid' ? (
                    <div
                      className={`${accent} rounded-lg p-2 shadow-inner shadow-main-900/10`}
                    >
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  ) : (
                    <Icon className="h-8 w-8 text-main-500" />
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {item.title}
                  </h3>
                </div>

                <span className="text-4xl font-bold tracking-tight text-main-500">
                  {item.value}
                </span>
              </div>
            ) : (
              // SPREAD LAYOUT
              <div className="relative flex items-center justify-between px-5 py-5">
                <div className="flex items-center gap-4">
                  {iconStyle === 'solid' ? (
                    <div
                      className={`${accent} rounded-xl p-3 shadow-inner shadow-main-900/10`}
                    >
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  ) : (
                    <Icon className="h-8 w-8 text-main-500" />
                  )}
                  <div className="flex flex-col text-left">
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      Resumen
                    </p>
                    <h3 className="text-sm font-semibold text-gray-900 truncate max-w-[12rem]">
                      {item.title}
                    </h3>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-3xl font-semibold text-main-500 tracking-tight">
                    {item.value}
                  </span>
                </div>
              </div>
            )}
          </article>
        )
      })}
    </div>
  )
}
